// Particle-to-bottle formation animation.
// Samples pixels from a bottle image and converges thousands of glowing particles into that shape.
// Usage:
//   const pb = new ParticleBottle(canvasEl, {
//     src: 'assets/products/all-purpose-pomegranate.png',
//     colors: ['#ec2a83', '#a05fb0', '#ffd23f', '#5fb4d8'],
//   });
//   pb.start();
//   pb.reform(); // re-run the formation animation

(function () {
  class ParticleBottle {
    constructor(canvas, opts = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.opts = Object.assign({
        src: null,
        colors: ['#ec2a83', '#a05fb0', '#ffd23f', '#5fb4d8', '#4fa856', '#3fb8b0'],
        density: 6,           // sample every Nth pixel (lower = more particles)
        formDuration: 2200,   // ms for particles to converge
        formDelay: 0,
        idleTwinkle: true,
      }, opts);
      this.particles = [];
      this.targets = [];
      this.imageLoaded = false;
      this.startTime = 0;
      this.running = false;
      this.raf = null;
      this._resize();
      window.addEventListener('resize', () => this._resize());
    }

    _resize() {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const rect = this.canvas.getBoundingClientRect();
      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;
      this.width = rect.width;
      this.height = rect.height;
      this.dpr = dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    setSource(src) {
      this.opts.src = src;
      this.imageLoaded = false;
      this._loadImage();
    }

    _loadImage() {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.image = img;
        this._sampleTargets();
        this.imageLoaded = true;
        if (this.pendingReform) {
          this.pendingReform = false;
          this.reform();
        }
      };
      img.onerror = () => console.warn('particle-bottle: failed to load', this.opts.src);
      img.src = this.opts.src;
    }

    _sampleTargets() {
      // Draw image at a small size to sample pixels
      const off = document.createElement('canvas');
      const scale = 0.25; // sample from a scaled-down version for perf
      const sw = Math.round(this.image.naturalWidth * scale);
      const sh = Math.round(this.image.naturalHeight * scale);
      off.width = sw;
      off.height = sh;
      const octx = off.getContext('2d');
      octx.drawImage(this.image, 0, 0, sw, sh);
      const data = octx.getImageData(0, 0, sw, sh).data;

      // Figure out target draw size to fit canvas
      const maxW = this.width * 0.85;
      const maxH = this.height * 0.85;
      const imgAR = this.image.naturalWidth / this.image.naturalHeight;
      let drawW = maxW, drawH = maxW / imgAR;
      if (drawH > maxH) { drawH = maxH; drawW = maxH * imgAR; }
      const originX = (this.width - drawW) / 2;
      const originY = (this.height - drawH) / 2;
      const step = this.opts.density;

      const targets = [];
      const colors = this.opts.colors;
      for (let y = 0; y < sh; y += step) {
        for (let x = 0; x < sw; x += step) {
          const i = (y * sw + x) * 4;
          const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
          // Skip transparent or near-white pixels (background)
          if (a < 100) continue;
          const luminance = (r*0.299 + g*0.587 + b*0.114);
          if (luminance > 240) continue; // skip white bg
          const tx = originX + (x / sw) * drawW;
          const ty = originY + (y / sh) * drawH;
          // Pick color: brighter pixels get accent colors, mid-tone get product color
          let color;
          if (Math.random() < 0.65) {
            // sampled pixel color (slightly saturated)
            color = `rgb(${r},${g},${b})`;
          } else {
            color = colors[Math.floor(Math.random() * colors.length)];
          }
          targets.push({ x: tx, y: ty, color, size: 1 + Math.random() * 2.2 });
        }
      }
      this.targets = targets;
      this._createParticles();
    }

    _createParticles() {
      // Create particles from random offscreen positions matching each target
      this.particles = this.targets.map(t => {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.max(this.width, this.height) * (0.6 + Math.random() * 0.7);
        return {
          sx: this.width/2 + Math.cos(angle) * dist,
          sy: this.height/2 + Math.sin(angle) * dist,
          tx: t.x, ty: t.y,
          x: 0, y: 0,
          color: t.color,
          size: t.size,
          delay: Math.random() * 350,
          twinkle: Math.random() * Math.PI * 2,
          idleSpeed: 0.001 + Math.random() * 0.003,
          arcSign: Math.random() > 0.5 ? 1 : -1,
          // ~35% render as sparkle stars (rest as round glow dots) for texture mix
          isStar: Math.random() < 0.35,
        };
      });
    }

    start() {
      if (this.opts.src && !this.imageLoaded) this._loadImage();
      this.pendingReform = true;
      this.running = true;
      this._tick();
    }

    reform() {
      if (!this.imageLoaded) { this.pendingReform = true; return; }
      this._createParticles();
      this.startTime = performance.now() + this.opts.formDelay;
      this.running = true;
      if (!this.raf) this._tick();
    }

    stop() {
      this.running = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    _easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    _drawStar(cx, cy, outerR, innerR, color, alpha) {
      const ctx = this.ctx;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.translate(cx, cy);
      // Draw 4-point twinkle star
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
        const px = Math.cos(angle) * r;
        const py = Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      // Bright core glow
      ctx.globalAlpha = alpha * 0.5;
      ctx.beginPath();
      ctx.arc(0, 0, innerR * 1.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    _tick = () => {
      const ctx = this.ctx;
      const now = performance.now();

      // Clear with slight fade for motion trails
      ctx.clearRect(0, 0, this.width, this.height);

      const dur = this.opts.formDuration;
      ctx.globalCompositeOperation = 'lighter';

      for (const p of this.particles) {
        const elapsed = Math.max(0, now - this.startTime - p.delay);
        const t = Math.min(1, elapsed / dur);
        const e = this._easeOutCubic(t);
        // Curved fly-in path (add a slight arc) for that "twirling" magic
        const arc = Math.sin(e * Math.PI) * 40 * (p.arcSign || 1);
        p.x = p.sx + (p.tx - p.sx) * e + Math.sin(t * Math.PI * 2 + p.twinkle) * arc * 0.3;
        p.y = p.sy + (p.ty - p.sy) * e + arc * 0.4;

        let sizeMul = 1;
        let alpha = 1;
        if (t >= 1 && this.opts.idleTwinkle) {
          p.twinkle += p.idleSpeed * 16;
          alpha = 0.5 + Math.sin(p.twinkle) * 0.5;
          p.x += Math.sin(p.twinkle * 0.5) * 0.4;
          p.y += Math.cos(p.twinkle * 0.3) * 0.4;
          sizeMul = 0.7 + Math.abs(Math.sin(p.twinkle)) * 0.5;
        } else {
          alpha = 0.4 + e * 0.6;
          sizeMul = 0.6 + e * 0.5;
        }

        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        const r = p.size * sizeMul;
        // Draw as 4-point sparkle star (Cinderella-style twinkle)
        if (p.isStar && (t >= 0.7 || t >= 1)) {
          this._drawStar(p.x, p.y, r * 1.6, r * 0.35, p.color, alpha);
        } else {
          // Round glow dot (during fly-in)
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();
          if (t >= 0.4) {
            ctx.globalAlpha = alpha * 0.35;
            ctx.beginPath();
            ctx.arc(p.x, p.y, r * 2.8, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      if (this.running) this.raf = requestAnimationFrame(this._tick);
    }
  }

  window.ParticleBottle = ParticleBottle;
})();
